import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, RefreshCw, Calendar, Users, CheckCircle, Package, Filter, X, ChevronDown } from 'lucide-react';
import { searchOrders } from '../../../../services/api/salesOrderApi';
import OrderDetailDrawer from '../shared/OrderDetailDrawer';

// Backend sends LocalDateTime (no timezone) but stores in UTC.
// Appending 'Z' tells JS to treat it as UTC so toLocaleString converts to user's local tz.
const parseUtcDate = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr);
    return new Date(s.endsWith('Z') ? s : s + 'Z');
};

const DATE_FILTERS = [
    { id: 'all', label: 'All Time' },
    { id: 'today', label: 'Today' },
    { id: '7d', label: 'Last 7 Days' },
    { id: '30d', label: 'Last 30 Days' },
];

const CONFIRMED_STATUS_CODES = ['CONFIRMED', 'APPROVED', 'REVISED', 'READY', 'PACKED'];

const WarehouseConfirmedView = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('all');
    const [salespersonFilter, setSalespersonFilter] = useState('All');
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Infinite Scroll States
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalOrdersCount, setTotalOrdersCount] = useState(0);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const result = await searchOrders({
                statusCodes: CONFIRMED_STATUS_CODES
            }, { page: 0, size: 15, sort: 'updatedAt,desc' });
            setOrders(result.orders || []);
            setTotalOrdersCount(result.totalElements || 0);
            setHasMore(result.orders?.length < (result.totalElements || 0));
            setPage(0);
        } catch (err) {
            console.error('Failed to load orders:', err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            const result = await searchOrders({
                statusCodes: CONFIRMED_STATUS_CODES
            }, { page: nextPage, size: 15, sort: 'updatedAt,desc' });
            
            const newOrders = result.orders || [];
            if (newOrders.length > 0) {
                setOrders(prev => {
                    const existingIds = new Set(prev.map(o => o.id));
                    const filteredNew = newOrders.filter(o => !existingIds.has(o.id));
                    const combined = [...prev, ...filteredNew];
                    setHasMore(combined.length < (result.totalElements || 0));
                    return combined;
                });
                setPage(nextPage);
            } else {
                setHasMore(false);
            }
        } catch (e) {
            console.error('Failed to load more orders:', e);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            loadMore();
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Extract unique salesperson names
    const salespersons = useMemo(() => {
        const names = new Set();
        orders.forEach(o => {
            const name = o.salesman || o.assignedSalesPersonName;
            if (name) names.add(name);
        });
        return ['All', ...Array.from(names).sort()];
    }, [orders]);

    // Filter to only confirmed orders, then apply date/salesperson/search filters
    const filteredOrders = useMemo(() => {
        const filtered = orders.filter(o => {
            // Status filter
            if (!CONFIRMED_STATUS_CODES.includes(o.status?.toUpperCase())) return false;

            // Date filter — use updatedAt (reflects confirmation date)
            if (dateFilter !== 'all' && o.updatedAt) {
                const orderDate = parseUtcDate(o.updatedAt);
                const now = new Date();
                const diffMs = now - orderDate;
                const diffDays = diffMs / (1000 * 60 * 60 * 24);
                if (dateFilter === 'today' && diffDays > 1) return false;
                if (dateFilter === '7d' && diffDays > 7) return false;
                if (dateFilter === '30d' && diffDays > 30) return false;
            }

            // Salesperson filter
            if (salespersonFilter !== 'All') {
                const name = o.salesman || o.assignedSalesPersonName || '';
                if (name !== salespersonFilter) return false;
            }

            // Search filter
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matchesSearch =
                    (o.id || '').toLowerCase().includes(term) ||
                    (o.orderRef || '').toLowerCase().includes(term) ||
                    (o.customer?.name || '').toLowerCase().includes(term) ||
                    (o.customer?.phone || '').includes(term) ||
                    (o.salesman || '').toLowerCase().includes(term) ||
                    (o.assignedSalesPersonName || '').toLowerCase().includes(term) ||
                    (o.delegatedSalesPersonName || '').toLowerCase().includes(term) ||
                    (o.assignedSalesPersonVehicleNumber || '').toLowerCase().includes(term) ||
                    (o.items || []).some(item => (item.name || item.productName || item.product || '').toLowerCase().includes(term));
                if (!matchesSearch) return false;
            }

            return true;
        });
        // Sort: latest confirmed first
        return filtered.sort((a, b) => {
            const da = parseUtcDate(b.updatedAt);
            const db = parseUtcDate(a.updatedAt);
            return (da || 0) - (db || 0);
        });
    }, [orders, dateFilter, salespersonFilter, searchTerm]);

    return (
        <div className="h-full flex flex-col bg-[#F9FAFB] relative overflow-hidden">
            {/* Header */}
            <div className="flex-shrink-0 px-6 pt-6 pb-4 bg-white border-b border-gray-100">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center">
                                <CheckCircle size={22} className="text-emerald-600" />
                            </div>
                            <span>Confirmed Orders</span>
                            {totalOrdersCount > 0 && (
                                <span className="text-sm font-bold bg-emerald-100 text-emerald-700 px-2.5 py-0.5 rounded-full">
                                    {totalOrdersCount} Total
                                </span>
                            )}
                        </h1>
                        <p className="text-sm font-medium text-gray-400 mt-1 ml-[52px]">
                            Orders confirmed by warehouse or sales manager
                        </p>
                    </div>
                    <button
                        onClick={fetchOrders}
                        className={`p-2.5 rounded-xl text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all duration-200 ${loading ? 'bg-emerald-50' : ''}`}
                        title="Refresh"
                    >
                        <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by customer, order ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all placeholder:text-gray-400"
                        />
                    </div>

                    {/* Date Filter */}
                    <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-0.5">
                        {DATE_FILTERS.map(df => (
                            <button
                                key={df.id}
                                onClick={() => setDateFilter(df.id)}
                                className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                                    dateFilter === df.id
                                        ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-black/5'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {df.label}
                            </button>
                        ))}
                    </div>

                    {/* Salesperson Filter */}
                    <div className="relative">
                        <select
                            value={salespersonFilter}
                            onChange={(e) => setSalespersonFilter(e.target.value)}
                            className="appearance-none pl-10 pr-8 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-700 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all cursor-pointer"
                        >
                            {salespersons.map(sp => (
                                <option key={sp} value={sp}>
                                    {sp === 'All' ? 'All Salespersons' : sp}
                                </option>
                            ))}
                        </select>
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                    </div>
                </div>

                {/* Active filter indicator */}
                {(dateFilter !== 'all' || salespersonFilter !== 'All' || searchTerm) && (
                    <div className="flex items-center gap-2 mt-3">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Filters:</span>
                        {dateFilter !== 'all' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold">
                                <Calendar size={12} />
                                {DATE_FILTERS.find(d => d.id === dateFilter)?.label}
                                <button onClick={() => setDateFilter('all')} className="ml-1 hover:text-emerald-900"><X size={12} /></button>
                            </span>
                        )}
                        {salespersonFilter !== 'All' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold">
                                <Users size={12} />
                                {salespersonFilter}
                                <button onClick={() => setSalespersonFilter('All')} className="ml-1 hover:text-blue-900"><X size={12} /></button>
                            </span>
                        )}
                        {searchTerm && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-bold">
                                <Search size={12} />
                                "{searchTerm}"
                                <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-gray-900"><X size={12} /></button>
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Orders List */}
            <div 
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto px-6 pt-4 pb-8 custom-scrollbar"
            >
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32">
                        <div className="w-16 h-16 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin mb-6" />
                        <p className="text-lg font-bold text-gray-400">Loading confirmed orders...</p>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center bg-white rounded-3xl border border-dashed border-gray-200">
                        <div className="w-24 h-24 rounded-3xl bg-emerald-50 flex items-center justify-center mb-6">
                            <CheckCircle size={48} className="text-emerald-200" />
                        </div>
                        <h3 className="text-xl font-black text-gray-900 mb-2">No confirmed orders</h3>
                        <p className="text-sm font-medium text-gray-400 max-w-xs mx-auto">
                            {searchTerm || dateFilter !== 'all' || salespersonFilter !== 'All'
                                ? 'Try adjusting your filters to see more results.'
                                : 'Orders will appear here once confirmed by the warehouse or sales team.'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-4 px-1">
                            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">
                                {filteredOrders.length} confirmed order{filteredOrders.length !== 1 ? 's' : ''}
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                            {/* Table Header — desktop shows 5 cols, tablet shows 4 */}
                            <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_140px_80px_120px_110px] gap-4 px-5 py-3 bg-gray-50 border-b border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                <span>Customer &amp; Order</span>
                                <span>Salesperson</span>
                                <span className="text-center">Items</span>
                                <span className="text-right">Total (Incl. VAT)</span>
                                <span className="text-right">Confirmed</span>
                            </div>
                            {/* Mobile/Tablet header */}
                            <div className="grid lg:hidden grid-cols-[minmax(0,1fr)_64px_110px_90px] gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                <span>Customer &amp; Order</span>
                                <span className="text-center">Items</span>
                                <span className="text-right">Total (Incl. VAT)</span>
                                <span className="text-right">Date</span>
                            </div>

                            {/* Table Rows */}
                            {filteredOrders.map((order, idx) => {
                                const confirmedDate = parseUtcDate(order.updatedAt);
                                const salesperson = order.salesman || order.assignedSalesPersonName;
                                return (
                                    <div
                                        key={order.id}
                                        onClick={() => setSelectedOrder(order)}
                                        className={`cursor-pointer hover:bg-emerald-50/30 transition-colors ${
                                            idx < filteredOrders.length - 1 ? 'border-b border-gray-50' : ''
                                        }`}
                                    >
                                        {/* Desktop row — 5 cols */}
                                        <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_140px_80px_110px_110px] gap-4 px-5 py-3.5 items-center">
                                            {/* Customer */}
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                                                    {order.customer?.name ? order.customer.name.charAt(0).toUpperCase() : '?'}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-bold text-gray-900 truncate leading-tight">
                                                        {order.customer?.name || 'Unknown'}
                                                    </p>
                                                    <p className="text-[11px] text-gray-400 font-medium truncate">
                                                        {order.orderRef || `#${(order.id || '').slice(-8)}`}
                                                    </p>
                                                </div>
                                            </div>
                                            {/* Salesperson */}
                                            <span className="text-xs font-medium text-gray-600 truncate">
                                                {salesperson || '—'}
                                            </span>
                                            {/* Items */}
                                            <div className="flex justify-center">
                                                <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg whitespace-nowrap">
                                                    {order.items?.length || 0}
                                                </span>
                                            </div>
                                            {/* Total */}
                                            <span className="text-sm font-black text-gray-900 text-right whitespace-nowrap">
                                                {new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(order.totalAmount || 0)}
                                            </span>
                                            {/* Date */}
                                            <span className="text-[11px] font-medium text-gray-400 text-right whitespace-nowrap">
                                                {confirmedDate
                                                    ? <><span>{confirmedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>{' '}<span className="text-gray-300">{confirmedDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span></>
                                                    : '—'}
                                            </span>
                                        </div>

                                        {/* Mobile/Tablet row — 4 cols, salesperson inside customer cell */}
                                        <div className="grid lg:hidden grid-cols-[minmax(0,1fr)_64px_100px_90px] gap-3 px-4 py-3 items-center">
                                            {/* Customer + salesperson stacked */}
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center text-emerald-700 font-bold text-sm flex-shrink-0">
                                                    {order.customer?.name ? order.customer.name.charAt(0).toUpperCase() : '?'}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-bold text-gray-900 truncate leading-tight">
                                                        {order.customer?.name || 'Unknown'}
                                                    </p>
                                                    <p className="text-[10px] text-gray-400 font-medium truncate">
                                                        {order.orderRef || `#${(order.id || '').slice(-8)}`}
                                                        {salesperson && <span className="text-blue-500 ml-1">· {salesperson}</span>}
                                                    </p>
                                                </div>
                                            </div>
                                            {/* Items */}
                                            <div className="flex justify-center">
                                                <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-lg whitespace-nowrap">
                                                    {order.items?.length || 0}
                                                </span>
                                            </div>
                                            {/* Total */}
                                            <span className="text-sm font-black text-gray-900 text-right whitespace-nowrap">
                                                {new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(order.totalAmount || 0)}
                                            </span>
                                            {/* Date */}
                                            <span className="text-[10px] font-medium text-gray-400 text-right whitespace-nowrap">
                                                {confirmedDate
                                                    ? confirmedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                                                    : '—'}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Order Detail Drawer */}
            {selectedOrder && (
                <OrderDetailDrawer
                    order={selectedOrder}
                    transitions={[]}
                    onTransition={() => {}}
                    loadingTransition={false}
                    onClose={() => setSelectedOrder(null)}
                    role="Warehouse"
                />
            )}
        </div>
    );
};

export default WarehouseConfirmedView;
